import request from './request';

// 通用 blob 下载（复用带拦截器的 axios 实例，401/501 走统一处理）
async function downloadBlob(path, params, filename) {
  const res = await request.get(path, { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(res);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// 认证
export const auth = {
  login: (data) => request.post('/auth/login', data),
  me: () => request.get('/auth/me'),
  changePassword: (data) => request.post('/auth/change-password', data),
};

// 用户管理
export const users = {
  list: (params) => request.get('/users', { params }),
  create: (data) => request.post('/users', data),
  update: (id, data) => request.put(`/users/${id}`, data),
  resetPassword: (id, data) => request.put(`/users/${id}/password`, data),
};

// 空间
export const spaces = {
  list: () => request.get('/spaces'),
  detail: (id) => request.get(`/spaces/${id}`),
  create: (data) => request.post('/spaces', data),
  update: (id, data) => request.put(`/spaces/${id}`, data),
  delete: (id) => request.delete(`/spaces/${id}`),
};

// 表计
export const meters = {
  list: (params) => request.get('/meters', { params }),
  detail: (id) => request.get(`/meters/${id}`),
  updateModbusConfig: (id, data) => request.put(`/meters/${id}/modbus-config`, data),
  testModbus: (id) => request.post(`/meters/${id}/test-modbus`),
  registerMapTemplate: () => request.get('/meters/modbus/register-map-template'),
};

// 实时监测
export const monitoring = {
  realtime: (meterId) => request.get(`/monitoring/realtime/${meterId}`),
  batchRealtime: (data) => request.post('/monitoring/realtime/batch', data),
  powerQuality: (meterId) => request.get(`/monitoring/power-quality/${meterId}`),
  collectorStatus: () => request.get('/monitoring/collector-status'),
  edgeStatus: () => request.get('/monitoring/edge-status'),
  curve: (params) => request.get('/monitoring/curve', { params }),
};

// 视频监控
export const video = {
  status: () => request.get('/video/status'),
  list: () => request.get('/video/cameras'),
  create: (data) => request.post('/video/cameras', data),
  update: (id, data) => request.put(`/video/cameras/${id}`, data),
  remove: (id) => request.delete(`/video/cameras/${id}`),
  playCredentials: (id) => request.get(`/video/cameras/${id}/play-credentials`),
};

// 视频融合标定
export const cameraCalibrations = {
  get: (cameraId) => request.get(`/video/calibrations/${cameraId}`),
  updateParams: (cameraId, data) => request.put(`/video/calibrations/${cameraId}`, data),
  addPoint: (cameraId, data) => request.post(`/video/calibrations/${cameraId}/points`, data),
  deletePoint: (pointId) => request.delete(`/video/calibrations/points/${pointId}`),
  compute: (cameraId) => request.post(`/video/calibrations/${cameraId}/compute`),
};

// 光伏
export const photovoltaic = {
  list: () => request.get('/photovoltaic'),
  prediction: (params) => request.get('/photovoltaic/prediction', { params }),
  realtime: () => request.get('/photovoltaic/realtime'),
};

// 储能
export const storage = {
  list: () => request.get('/storage'),
  strategy: (params) => request.get('/storage/strategy', { params }),
  setStrategy: (data) => request.post('/storage/strategy', data),
  dualMode: (params) => request.get('/storage/dual-mode', { params }),
  setDualMode: (data) => request.post('/storage/dual-mode', data),
  // 经济调度（日前充放电计划优化，仅建议不下发）
  econPlanGenerate: (data) => request.post('/storage/econ-dispatch/plan', data),
  econPlanByDate: (params) => request.get('/storage/econ-dispatch/plan', { params }),
  econLatest: () => request.get('/storage/econ-dispatch/latest'),
  econCompare: (params) => request.get('/storage/econ-dispatch/compare', { params }),
};

// 策略模板库 + 规则引擎
export const strategyTemplates = {
  list: (params) => request.get('/strategy-templates', { params }),
  create: (data) => request.post('/strategy-templates', data),
  update: (id, data) => request.put(`/strategy-templates/${id}`, data),
  toggle: (id, data) => request.post(`/strategy-templates/${id}/toggle`, data),
  remove: (id) => request.delete(`/strategy-templates/${id}`),
  simulate: (data) => request.post('/strategy-templates/simulate', data),
  evaluation: () => request.get('/strategy-templates/evaluation'),
};

// 充电桩
export const charging = {
  list: () => request.get('/charging'),
  status: () => request.get('/charging/status'),
};

// 能耗分析
export const energy = {
  analysis: (params) => request.get('/energy/analysis', { params }),
  hierarchy: (params) => request.get('/energy/hierarchy', { params }),
};

// 需量管理
export const demand = {
  overview: () => request.get('/demand/overview'),
  trend: (params) => request.get('/demand/trend', { params }),
  curve: (params) => request.get('/demand/curve', { params }),
  suggestion: () => request.get('/demand/suggestion'),
  settings: () => request.get('/demand/settings'),
  updateSettings: (data) => request.put('/demand/settings', data),
};

// 告警
export const alarms = {
  list: (params) => request.get('/alarms', { params }),
  rules: () => request.get('/alarms/rules'),
  createRule: (data) => request.post('/alarms/rules', data),
  acknowledge: (id, data) => request.post(`/alarms/${id}/acknowledge`, data),
  createWorkOrder: (id) => request.post(`/alarms/${id}/work-order`),
  stats: () => request.get('/alarms/stats'),
  trend: (params) => request.get('/alarms/trend', { params }),
  // 告警知识库
  knowledgeList: () => request.get('/alarms/knowledge'),
  createKnowledge: (data) => request.post('/alarms/knowledge', data),
  updateKnowledge: (id, data) => request.put(`/alarms/knowledge/${id}`, data),
  deleteKnowledge: (id) => request.delete(`/alarms/knowledge/${id}`),
  importKnowledge: (items) => request.post('/alarms/knowledge/import', { items }),
};

// 运维工单
export const workOrders = {
  list: (params) => request.get('/work-orders', { params }),
  overview: () => request.get('/work-orders/overview'),
  detail: (id) => request.get(`/work-orders/${id}`),
  assign: (id, data) => request.post(`/work-orders/${id}/assign`, data),
  start: (id) => request.post(`/work-orders/${id}/start`),
  complete: (id, data) => request.post(`/work-orders/${id}/complete`, data),
  close: (id, data) => request.post(`/work-orders/${id}/close`, data),
};

// 运维小程序
export const mobile = {
  home: () => request.get('/mobile/home'),
  tasks: (params) => request.get('/mobile/tasks', { params }),
  acceptTask: (id) => request.post(`/mobile/tasks/${id}/accept`),
  completeTask: (id, data) => request.post(`/mobile/tasks/${id}/complete`, data),
  alarms: () => request.get('/mobile/alarms'),
  acknowledgeAlarm: (id, data) => request.post(`/mobile/alarms/${id}/acknowledge`, data),
  inspectionTemplates: () => request.get('/mobile/inspections/templates'),
  inspections: (params) => request.get('/mobile/inspections', { params }),
  inspectionDetail: (id) => request.get(`/mobile/inspections/${id}`),
  submitInspection: (id, data) => request.post(`/mobile/inspections/${id}/submit`, data),
  createInspection: (data) => request.post('/mobile/inspections', data),
};

// 能碳管理
export const carbon = {
  overview: (params) => request.get('/carbon/overview', { params }),
  trends: (params) => request.get('/carbon/trends', { params }),
  breakdown: (params) => request.get('/carbon/breakdown', { params }),
  targets: (params) => request.get('/carbon/targets', { params }),
  assets: () => request.get('/carbon/assets'),
  footprint: () => request.get('/carbon/footprint'),
};

// 碳模块 v1：减排台账 / 核算引擎 / 资产登记 / 折算因子
export const carbonLedger = {
  summary: () => request.get('/carbon/ledger/summary'),
  list: (params) => request.get('/carbon/ledger', { params }),
  create: (data) => request.post('/carbon/ledger', data),
  sync: () => request.post('/carbon/ledger/sync'),
  factors: () => request.get('/carbon/factors'),
  updateFactor: (key, value) => request.put(`/carbon/factors/${key}`, { value }),
  createAsset: (data) => request.post('/carbon/assets', data),
  updateAsset: (id, data) => request.put(`/carbon/assets/${id}`, data),
};

// 报表
export const reports = {
  generate: (params) => request.get('/reports/generate', { params }),
  templates: () => request.get('/reports/templates'),
  export: (params) => downloadBlob('/reports/export', params, `能耗报表_${params.start_date || 'today'}.xlsx`),
  comparison: (params) => request.get('/reports/comparison', { params }),
  ranking: (params) => request.get('/reports/ranking', { params }),
  assetMonthly: (params) => request.get('/reports/asset-monthly', { params }),
  assetMonthlyExport: (params) => downloadBlob('/reports/asset-monthly/export', params, `资方月报_${params.month || '本月'}.xlsx`),
};

// 配储测算（售前工具，纯计算不落库，结果为估算仅供投资参考）
export const storageCalc = {
  defaults: () => request.get('/storage/calc/defaults'),
  calc: (data) => request.post('/storage/calc', data),
};

// AI 预测
export const prediction = {
  status: () => request.get('/prediction/status'),
  load: (params) => request.get('/prediction/load', { params }),
  pv: (params) => request.get('/prediction/pv', { params }),
  price: (params) => request.get('/prediction/price', { params }),
};

// 操作审计
export const audit = {
  logs: (params) => request.get('/audit/logs', { params }),
  stats: () => request.get('/audit/stats'),
};

// 大屏
export const dashboardApi = {
  parks: () => request.get('/dashboard/parks'),
  overview: (params) => request.get('/dashboard/overview', { params }),
  energyFlow: (params) => request.get('/dashboard/energy-flow', { params }),
  liveAlarms: (params) => request.get('/dashboard/live-alarms', { params }),
  trend: (params) => request.get('/dashboard/trend', { params }),
  mapStations: () => request.get('/dashboard/map-stations'),
  topStations: (params) => request.get('/dashboard/top-stations', { params }),
  trend30d: (params) => request.get('/dashboard/trend-30d', { params }),
};

// VPP 资源
export const vppResources = {
  list: (params) => request.get('/vpp/resources', { params }),
  aggregate: () => request.get('/vpp/resources/aggregate'),
};

// VPP 调度
export const vppDispatch = {
  list: (params) => request.get('/vpp/dispatch', { params }),
  create: (data) => request.post('/vpp/dispatch', data),
  accept: (id) => request.post(`/vpp/dispatch/${id}/accept`),
  execute: (id) => request.post(`/vpp/dispatch/${id}/execute`),
};

// VPP 市场
export const vppMarket = {
  list: (params) => request.get('/vpp/market', { params }),
  overview: () => request.get('/vpp/market/market-overview'),
};

// VPP 需求响应
export const vppDemand = {
  // 事件管理
  list: (params) => request.get('/vpp/demand/events', { params }),
  create: (data) => request.post('/vpp/demand/events', data),
  clear: (id, declarationIds) => request.post(`/vpp/demand/events/${id}/clear`, { declaration_ids: declarationIds }),
  start: (id) => request.post(`/vpp/demand/events/${id}/start`),
  assess: (id) => request.post(`/vpp/demand/events/${id}/assess`),
  cancel: (id) => request.post(`/vpp/demand/events/${id}/cancel`),
  // 申报
  declare: (id, data) => request.post(`/vpp/demand/events/${id}/declarations`, data),
  declarations: (id) => request.get(`/vpp/demand/events/${id}/declarations`),
  // 响应监控
  monitoring: (id) => request.get(`/vpp/demand/events/${id}/monitoring`),
  // 事件基线预览/重算（n/adjust_factor/exclude_dates 可选）
  eventBaseline: (id, params) => request.get(`/vpp/demand/events/${id}/baseline`, { params }),
  // 基线
  baselines: () => request.get('/vpp/demand/baselines'),
  computeBaselines: () => request.post('/vpp/demand/baselines/compute'),
  // 考核与收益
  assessments: () => request.get('/vpp/demand/assessments'),
  revenueSummary: () => request.get('/vpp/demand/revenue/summary'),
};

// VPP 结算偏差与交易复盘
export const vppReview = {
  forecastDeviation: (params) => request.get('/vpp/review/deviation/forecast', { params }),
  clearingDeviation: (params) => request.get('/vpp/review/deviation/clearing', { params }),
  demandDeviation: (params) => request.get('/vpp/review/deviation/demand', { params }),
  daily: (params) => request.get('/vpp/review/daily', { params }),
  monthly: (params) => request.get('/vpp/review/monthly', { params }),
};

// VPP 结算
export const vppSettlement = {
  list: (params) => request.get('/vpp/settlement', { params }),
  overview: () => request.get('/vpp/settlement/overview'),
  export: (params) => downloadBlob('/vpp/settlement/export', params, `VPP结算明细_${params?.period || '全部'}_${new Date().toISOString().slice(0, 10)}.xlsx`),
};

// VPP 客户与合同管理（CRM 层）
export const vppCustomers = {
  list: (params) => request.get('/vpp/customers', { params }),
  detail: (id) => request.get(`/vpp/customers/${id}`),
  create: (data) => request.post('/vpp/customers', data),
  update: (id, data) => request.put(`/vpp/customers/${id}`, data),
  delete: (id) => request.delete(`/vpp/customers/${id}`),
  contracts: (params) => request.get('/vpp/customers/contracts', { params }),
  contractDetail: (id) => request.get(`/vpp/customers/contracts/${id}`),
  createContract: (data) => request.post('/vpp/customers/contracts', data),
  updateContract: (id, data) => request.put(`/vpp/customers/contracts/${id}`, data),
  deleteContract: (id) => request.delete(`/vpp/customers/contracts/${id}`),
};

// VPP AI 报价
export const vppQuotation = {
  list: () => request.get('/vpp/quotation'),
  aiGenerate: (params) => request.get('/vpp/quotation/ai-generate', { params }),
  backtest: (params) => request.get('/vpp/quotation/backtest', { params }),
  marketTypes: () => request.get('/vpp/quotation/market-types'),
};

// 数字孪生
export const digitalTwin = {
  topology: (params) => request.get('/digital-twin/topology', { params }),
};

// 多租户 SaaS
export const tenants = {
  list: () => request.get('/tenants'),
  current: () => request.get('/tenants/current'),
  detail: (id) => request.get(`/tenants/${id}`),
  create: (data) => request.post('/tenants', data),
  switch: (data) => request.post('/tenants/switch', data),
};

// IEC 61850 协议
export const protocols = {
  iec61850Status: () => request.get('/protocols/iec61850/status'),
  iec61850Discover: (params) => request.get('/protocols/iec61850/discover', { params }),
  iec61850Read: (iedName) => request.get(`/protocols/iec61850/read/${iedName}`),
  iec61850MapMeter: (meterId) => request.post(`/protocols/iec61850/map-meter/${meterId}`),
};

// VPP 五步交易闭环
export const vppTradeCycle = {
  overview: () => request.get('/vpp/trade-cycle/overview'),
  list: (params) => request.get('/vpp/trade-cycle', { params }),
  detail: (cycleId) => request.get(`/vpp/trade-cycle/${cycleId}`),
  aiCurve: (params) => request.get('/vpp/trade-cycle/ai-curve', { params }),
  create: (data) => request.post('/vpp/trade-cycle', data),
  clearing: (cycleId) => request.post(`/vpp/trade-cycle/${cycleId}/clearing`),
  execution: (cycleId) => request.post(`/vpp/trade-cycle/${cycleId}/execution`),
  evaluation: (cycleId) => request.post(`/vpp/trade-cycle/${cycleId}/evaluation`),
  settlement: (cycleId) => request.post(`/vpp/trade-cycle/${cycleId}/settlement`),
};

// 设备管理
export const devicesApi = {
  list: () => request.get('/devices'),
  health: () => request.get('/devices/health'),
};

